import { http, unwrap, unwrapList, type ActionResponse } from './http';
import { TRANSLATION_FORMAT } from '@features/translations/types';
import type {
  CreateTranslationInput,
  NamespaceMutationResult,
  NamespaceSummary,
  TranslationFormat,
  TranslationKey,
  TranslationListQuery,
  TranslationListResponse,
  TranslationStatus,
  UpdateTranslationInput,
} from '@features/translations/types';

/**
 * Backend /api/v1/localization/keys DTO.
 *
 * `format` arrives either as the string "Text"/"Markdown" or as a numeric enum
 * (0 = Text, 1 = Markdown) — both are handled defensively, defaulting to Text.
 * `status` follows the same int convention as glossary (7 = Published, else
 * Draft); a string "Published"/"Draft" is also accepted defensively.
 */
interface TranslationBeDto {
  id: string;
  namespace: string | null;
  key: string | null;
  languageCode: string | null;
  value: string | null;
  description: string | null;
  format: string | number | null;
  status: string | number | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateTranslationBeRequest {
  namespace: string;
  key: string;
  languageCode: string;
  value: string;
  description?: string | null;
  format: TranslationFormat;
  status: number;
}

type UpdateTranslationBeRequest = CreateTranslationBeRequest;

const BASE = '/api/v1/localization/keys';
const NAMESPACES_BASE = '/api/v1/localization/namespaces';

interface NamespaceSummaryBeDto {
  namespace: string | null;
  keyCount: number | null;
  languageCodes: string[] | null;
}

interface NamespaceMutationBeDto {
  namespace: string | null;
  newNamespace: string | null;
  affectedRows: number | null;
}

function mapNamespaceSummary(d: NamespaceSummaryBeDto): NamespaceSummary {
  return {
    namespace: d.namespace ?? '',
    keyCount: d.keyCount ?? 0,
    languageCodes: d.languageCodes ?? [],
  };
}

function mapNamespaceMutation(d: NamespaceMutationBeDto): NamespaceMutationResult {
  return {
    namespace: d.namespace ?? '',
    newNamespace: d.newNamespace ?? undefined,
    affectedRows: d.affectedRows ?? 0,
  };
}

/** BE status int (glossary convention) — 7 = Published, else Draft. */
const STATUS_PUBLISHED = 7;
const STATUS_DRAFT = 4;

function mapFormat(f: TranslationBeDto['format']): TranslationFormat {
  if (typeof f === 'number') return f === 1 ? TRANSLATION_FORMAT.MARKDOWN : TRANSLATION_FORMAT.TEXT;
  if (typeof f === 'string' && f.toLowerCase() === 'markdown') return TRANSLATION_FORMAT.MARKDOWN;
  return TRANSLATION_FORMAT.TEXT;
}

function mapStatus(s: TranslationBeDto['status']): TranslationStatus {
  if (typeof s === 'number') return s === STATUS_PUBLISHED ? 'PUBLISHED' : 'DRAFT';
  if (typeof s === 'string' && s.toLowerCase() === 'published') return 'PUBLISHED';
  return 'DRAFT';
}

function statusToInt(s: TranslationStatus): number {
  return s === 'PUBLISHED' ? STATUS_PUBLISHED : STATUS_DRAFT;
}

function mapTranslation(b: TranslationBeDto): TranslationKey {
  return {
    id: b.id,
    namespace: b.namespace ?? '',
    key: b.key ?? '',
    languageCode: b.languageCode ?? 'tr',
    value: b.value ?? '',
    description: b.description ?? undefined,
    format: mapFormat(b.format),
    status: mapStatus(b.status),
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

export const translationsApi = {
  async list(query: TranslationListQuery = {}): Promise<TranslationListResponse> {
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.q) params.search = query.q;
    if (query.namespace) params.namespace = query.namespace;
    if (query.languageCode) params.languageCode = query.languageCode;
    if (query.status) params.status = statusToInt(query.status);
    if (query.sort === 'key') {
      params.sortBy = 'key';
      params.descending = false;
    } else if (query.sort === 'namespace') {
      params.sortBy = 'namespace';
      params.descending = false;
    } else if (query.sort === 'recent') {
      params.sortBy = 'updatedAt';
      params.descending = true;
    }
    const res = await http.get<ActionResponse<TranslationBeDto>>(BASE, { params });
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapTranslation),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? (query.page ?? 1),
      pageSize: pagination?.pageSize ?? (query.pageSize ?? 20),
      totalPages: pagination?.totalPages ?? 1,
    };
  },

  async get(id: string): Promise<TranslationKey> {
    const dto = unwrap(await http.get<ActionResponse<TranslationBeDto>>(`${BASE}/${id}`));
    return mapTranslation(dto);
  },

  async create(input: CreateTranslationInput): Promise<TranslationKey> {
    const body: CreateTranslationBeRequest = {
      namespace: input.namespace,
      key: input.key,
      languageCode: input.languageCode,
      value: input.value,
      description: input.description ?? null,
      format: input.format,
      status: statusToInt(input.status),
    };
    const id = unwrap(await http.post<ActionResponse<string>>(BASE, body));
    const dto = unwrap(await http.get<ActionResponse<TranslationBeDto>>(`${BASE}/${id}`));
    return mapTranslation(dto);
  },

  async update(id: string, input: UpdateTranslationInput): Promise<TranslationKey> {
    const current = await this.get(id);
    const body: UpdateTranslationBeRequest = {
      namespace: input.namespace ?? current.namespace,
      key: input.key ?? current.key,
      languageCode: input.languageCode ?? current.languageCode,
      value: input.value ?? current.value,
      description: input.description ?? current.description ?? null,
      format: input.format ?? current.format,
      status: statusToInt(input.status ?? current.status),
    };
    await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body);
    const dto = unwrap(await http.get<ActionResponse<TranslationBeDto>>(`${BASE}/${id}`));
    return mapTranslation(dto);
  },

  async remove(id: string): Promise<void> {
    await http.delete<ActionResponse<unknown>>(`${BASE}/${id}`);
  },

  async bulkDelete(
    ids: string[],
  ): Promise<{ deleted: number; skipped: number; skippedIds: string[] }> {
    const dto = unwrap(
      await http.post<
        ActionResponse<{ deleted: number; skipped: number; skippedIds: string[] | null }>
      >(`${BASE}/bulk-delete`, { ids }),
    );
    return { deleted: dto.deleted, skipped: dto.skipped, skippedIds: dto.skippedIds ?? [] };
  },

  /**
   * Distinct namespaces with per-namespace key count + present language codes.
   * Replaces the former "sample the first 200 keys and dedupe" hack — the BE now
   * aggregates this server-side (`GET /localization/namespaces`).
   */
  async getNamespaces(): Promise<NamespaceSummary[]> {
    const res = await http.get<ActionResponse<NamespaceSummaryBeDto>>(NAMESPACES_BASE);
    const { items } = unwrapList(res);
    return items.map(mapNamespaceSummary);
  },

  /** Rename a namespace (moves every live key row). 409 NAMESPACE_MERGE_CONFLICT on collision. */
  async renameNamespace(name: string, newName: string): Promise<NamespaceMutationResult> {
    const dto = unwrap(
      await http.put<ActionResponse<NamespaceMutationBeDto>>(
        `${NAMESPACES_BASE}/${encodeURIComponent(name)}`,
        { newName },
      ),
    );
    return mapNamespaceMutation(dto);
  },

  /** Soft-delete every key row in a namespace. 404 NOT_FOUND if it has no live rows. */
  async deleteNamespace(name: string): Promise<NamespaceMutationResult> {
    const dto = unwrap(
      await http.delete<ActionResponse<NamespaceMutationBeDto>>(
        `${NAMESPACES_BASE}/${encodeURIComponent(name)}`,
      ),
    );
    return mapNamespaceMutation(dto);
  },
};
